import React, { useState, useRef } from "react";
import styled from "@emotion/styled";
import 'react-csv-importer/dist/index.css';
import SaveIcon from '@mui/icons-material/Save';
import CheckIcon from '@mui/icons-material/Check';
import ErrorIcon from "@mui/icons-material/Error";
import { useTheme } from "@mui/styles";
import BackspaceIcon from '@mui/icons-material/Backspace';
import { useSelector } from "react-redux";
import CircularProgress from "@mui/material/CircularProgress";
import { StyledTextArea } from "../../../../componentThemes.js";
import { selectUserFullName } from "../../../../slices/userInfoSlice.js";
import { selectViewingMortgage } from "../../../../slices/viewingMortgageSlice.js";
import { selectAddMortgageNotePerm } from "../../../../slices/userActionsPermissionsSlice.js";
import { useAddMortgageNoteMutation } from "../../../../slices/api/apiSlice.js";
import { Button, InputWrapper, ErrorWrapper } from "../../../../StyledComponents.js";


const Notes = ({ userId, leadData, requestOrigin, mortgagestatus }) => {
	const theme = useTheme()

  const addMortgageNotePerm = useSelector(selectAddMortgageNotePerm)
  const userFullName = useSelector(selectUserFullName)
  const mortgageData = useSelector(selectViewingMortgage)

  const [isError, setIsError] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [isFetching, setIsFetching] = useState(false)

  const [addMortgageNote] = useAddMortgageNoteMutation()

  const handleClearNotes = () => {
    setNotesCount(0)
    setStatusNotesLength(null)
    setStatusNotesCharacter(null)
    mortgageNotes.current.value = ''
  }

  const handleSaveNote = async () => {
    setIsFetching(true)
    setIsError(false)
    setIsSuccess(false)
    let currentNote = mortgageNotes.current.value 
    let resp = await addMortgageNote({
      leadId: leadData._id,
      note: currentNote,
      author: userFullName,
      mortgageId: mortgageData._id,
      userId: userId,
      requestOrigin: requestOrigin,
    })
    if (resp.error) {
      setIsError(true)
    } else {
      setIsSuccess(true)
      mortgageNotes.current.value = "";
    }
    setNotesCount(0)
    setIsFetching(false)
  }

  const [statusNotesCharacter, setStatusNotesCharacter] = useState(null);
  const [statusNotesLength, setStatusNotesLength] = useState(null);
  const mortgageNotes = useRef();
  const [notesCount, setNotesCount] = useState(0);

  const handleNotesCount = () => {
    setNotesCount(mortgageNotes.current.value.length);
		if (mortgageNotes.current.value.includes('<') || mortgageNotes.current.value.includes('>')) {
			setStatusNotesCharacter(true)
		} else {
			setStatusNotesCharacter(false)
		}
		if (mortgageNotes.current.value.length > 255) {
			setStatusNotesLength("tooLong")
		} else {
			setStatusNotesLength(null)
    }
  }

  return (
    <NotesPanel>
      {isSuccess ?
        <NotesHeader $tone="success">
          <HeaderIcon>
            <CheckIcon />
          </HeaderIcon>
          <HeaderText>Success</HeaderText>
        </NotesHeader>
      : isError ?
        <NotesHeader $tone="error">
          <HeaderIcon>
            <ErrorIcon />
          </HeaderIcon>
          <HeaderText>
            Failed... please try again. Reach out to support@lancastersweep.com if the problem persists.
          </HeaderText>
        </NotesHeader>
      :
        <NotesHeader>
          <HeaderText>Notes</HeaderText>
          <HeaderMeta>Audit and collaboration record</HeaderMeta>
        </NotesHeader>
      }
      {mortgageData !== undefined ?
        <NotesBody>
          <NotesScroll>
            {mortgageData.mortgageNotes.length > 0 ?
              <NotesList>
                {mortgageData.mortgageNotes.map((note) => (
                  <NoteItem key={note._id}>
                    <NoteContent>{note.content}</NoteContent>
                    <NoteMeta>- {note.author}, {note.date}</NoteMeta>
                  </NoteItem>
                ))}
              </NotesList>
            :
              <EmptyState>
                <EmptyTitle>No notes recorded</EmptyTitle>
                <EmptyCopy>
                  Add a note to document investigation context, handoffs, or follow-up decisions.
                </EmptyCopy>
              </EmptyState>
            }
          </NotesScroll>
          <Composer>
            <ComposerField>
              <ComposerHeader>
                <ComposerTitle>Add Note</ComposerTitle>
                <ComposerStatus>
                  {statusNotesCharacter === true && (
                    <ErrorWrapper>
                      <ErrorIcon />
                      {`Cannot include the characters " < " or " > "`}
                    </ErrorWrapper>
                  )}
                  {statusNotesLength === "tooLong" && (
                    <ErrorWrapper>
                      <ErrorIcon />
                      {`Note cannot be longet than 255 characters`}
                    </ErrorWrapper>
                  )}
                  {statusNotesLength === "tooShort" && (
                    <ErrorWrapper>
                      <ErrorIcon />
                      {`Cannot submit a blank note`}
                    </ErrorWrapper>
                  )}
                  <CharacterCount $invalid={statusNotesLength === "tooLong" || statusNotesCharacter === true}>
                    {notesCount}/255
                  </CharacterCount>
                </ComposerStatus>
              </ComposerHeader>
              <StyledTextArea
                tabIndex="1"
                autoFocus
                type="text"
                name="term"
                id="term"
                ref={mortgageNotes}
                disabled={mortgagestatus === 'inactive'}
                minRows={3}
                onChange={() => {
                  if (statusNotesLength === "tooShort") {
                    setStatusNotesLength(null)
                  }
                  handleNotesCount()
                }}
              />
            </ComposerField>
            <ComposerActions>
              <Button
                tabIndex="2"
                aria-label="Save note"
                onClick={isFetching || ((notesCount === 0 || statusNotesLength === 'tooLong' || statusNotesCharacter || !addMortgageNotePerm)) ? null : () => handleSaveNote()}
                className={isFetching ? "primaryLoading notes" : (notesCount === 0 || statusNotesLength === 'tooLong' || statusNotesCharacter || !addMortgageNotePerm) ? "primaryDisabled notes" : "primary notes"}
              >
                <SaveIcon />
              </Button>
              <Button
                tabIndex="3"
                aria-label="Clear note"
                onClick={() => handleClearNotes()}
                className={notesCount === 0 ? "neutral notes" : "neutral notes"}
              >
                <BackspaceIcon />
              </Button>
            </ComposerActions>
          </Composer>
        </NotesBody>
      :
        <LoadingState>
          <CircularProgress
            sx={{ color: `${theme.palette.primary.main}` }}
            size={36}
          />
        </LoadingState>
      }
    </NotesPanel>
  )
}

const NotesPanel = styled.section(({ theme }) => `
  width: 100%;
  height: 100%;
  max-height: 100%;
  min-width: 0;
  min-height: 0;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  background: ${theme.palette.common.cardBackground};
  overflow: hidden;
`)

const NotesHeader = styled.header(({ theme, $tone }) => {
  const isSuccess = $tone === "success";
  const isError = $tone === "error";
  const toneColor = isSuccess
    ? theme.palette.primary.dark
    : isError
      ? theme.palette.common.redDark
      : theme.palette.common.grayText;
  const toneBackground = isSuccess
    ? theme.palette.primary.labelTransparent
    : isError
      ? theme.palette.common.redTransparent
      : theme.palette.common.white;

  return `
    min-height: 4rem;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.7rem;
    padding: 0.8rem 1rem;
    border-bottom: 0.1rem solid ${theme.palette.common.grayBorder};
    background: ${toneBackground};
    color: ${toneColor};
  `;
})

const HeaderIcon = styled.div(({ theme }) => `
  width: 2.4rem;
  height: 2.4rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.6rem;
  background: ${theme.palette.common.white};
  border: 0.1rem solid currentColor;

  svg {
    width: 1.5rem;
    height: 1.5rem;
  }
`)

const HeaderText = styled.span(({ theme }) => `
  color: inherit;
  font-size: 1.05rem;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
  font-family: -apple-system, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", 'Inter', "Helvetica Neue", sans-serif;
`)

const HeaderMeta = styled.span(({ theme }) => `
  color: ${theme.palette.common.grayText};
  font-size: 0.9rem;
  font-weight: 600;
`)

const NotesBody = styled.div(({ theme }) => `
  min-height: 0;
  display: grid;
  grid-template-rows: minmax(0, 1fr) auto;
  background: ${theme.palette.common.grayBorderLight};
  overflow: hidden;
`)

const NotesScroll = styled.div(({ theme }) => `
  min-height: 0;
  height: 100%;
  max-height: 100%;
  padding: 1rem;
  overflow-x: hidden;
  overflow-y: auto;
  background: ${theme.palette.common.cardBackground};

  ::-webkit-scrollbar-thumb {
    border: 0.1rem solid ${theme.palette.common.grayText};
    background-color: ${theme.palette.common.grayText};
  }

  ::-webkit-scrollbar {
    -webkit-appearance: none;
    border: 0.05rem solid ${theme.palette.common.grayBorderDark};
    background-color: ${theme.palette.common.pageBackground};
  }

  ::-webkit-scrollbar:vertical {
    width: 0.4rem;
  }
`)

const NotesList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  max-width: 100%;
  margin: 0;
  padding: 0;
  list-style: none;
`

const NoteItem = styled.li(({ theme }) => `
  display: grid;
  gap: 0.55rem;
  padding: 0.9rem 1rem;
  border: 0.1rem solid ${theme.palette.common.grayBorder};
  border-left: 0.35rem solid ${theme.palette.primary.dark};
  border-radius: 0.8rem;
  background: ${theme.palette.common.white};
`)

const NoteContent = styled.div(({ theme }) => `
  min-width: 0;
  color: ${theme.palette.common.black};
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.55;
  overflow-wrap: break-word;
`)

const NoteMeta = styled.div(({ theme }) => `
  color: ${theme.palette.common.grayText};
  font-size: 0.85rem;
  font-weight: 700;
`)

const EmptyState = styled.div(({ theme }) => `
  min-height: 11rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  padding: 2rem;
  border: 0.1rem dashed ${theme.palette.common.grayBorderDark};
  border-radius: 0.8rem;
  background: ${theme.palette.common.white};
  text-align: center;
`)

const EmptyTitle = styled.div(({ theme }) => `
  color: ${theme.palette.common.black};
  font-size: 1.05rem;
  font-weight: 800;
`)

const EmptyCopy = styled.div(({ theme }) => `
  max-width: 28rem;
  color: ${theme.palette.common.grayText};
  font-size: 0.95rem;
  font-weight: 600;
  line-height: 1.45;
`)

const Composer = styled.div(({ theme }) => `
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 0.8rem;
  align-items: end;
  padding: 0.9rem 1rem;
  border-top: 0.1rem solid ${theme.palette.common.grayBorder};
  background: ${theme.palette.common.white};
`)

const ComposerField = styled(InputWrapper)`
  width: 100%;
  min-width: 0;
  margin: 0;
  align-items: stretch;

  textarea {
    min-height: 4.8rem;
    max-height: 8rem;
    border-color: ${({ theme }) => theme.palette.common.grayBorderDark};
    font-size: 1rem;
    line-height: 1.45;
    resize: none;
    overflow-y: auto;
  }
`

const ComposerHeader = styled.div`
  min-height: 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`

const ComposerTitle = styled.div(({ theme }) => `
  color: ${theme.palette.common.black};
  font-size: 0.9rem;
  font-weight: 800;
  text-transform: uppercase;
`)

const ComposerStatus = styled.div`
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
`

const CharacterCount = styled.div(({ theme, $invalid }) => `
  color: ${$invalid ? theme.accent.error.primary : theme.palette.common.grayText};
  font-size: 0.85rem;
  font-weight: 800;
  white-space: nowrap;
`)

const ComposerActions = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 0.5rem;
`

const LoadingState = styled.div`
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`

export default Notes;
